import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, Sun, Eye, Shield, FileText, ExternalLink, Info } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BBN_PHOTOS } from "@/lib/images";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

/**
 * Fokuserad landningssida för söktermerna "3m prestige 70" och
 * "solfilm 3m prestige 70". Bygger på verifierade värden från 3M:s
 * officiella produktblad (ref. 1400301O).
 */

const specs = [
  { label: "Synligt ljus (VLT)", value: "68 %", icon: Eye, note: "Nästan lika ljust som obehandlat glas (~88 %)." },
  { label: "Total solenergireduktion (TSER)", value: "50 %", icon: Sun, note: "Stoppar hälften av solvärmen innan den når rummet." },
  { label: "IR-blockering", value: "≈ 97 %", icon: Sun, note: "Reflekterar den infraröda värmestrålningen." },
  { label: "UV-blockering", value: "99 %", icon: Shield, note: "Skyddar möbler, parkett och konst från blekning." },
  { label: "Bländningsreduktion", value: "23 %", icon: Eye, note: "Mindre bländning när du tittar på en skärm eller ut genom fönstret, utan mörka rum." },
  { label: "Reflektion utifrån", value: "Låg", icon: Check, note: "Fasaden behåller ett neutralt utseende utan spegeleffekt." },
];

const faqs = [
  {
    q: "Vad är skillnaden mellan 3M Prestige 70 och vanlig solfilm?",
    a: "Prestige 70 är en nanokeramisk multilagerfilm med över 200 mikroskopiska polymerlager reflekterar värme och UV utan metaller. Vanlig tonad film använder en metallbeläggning som ger mörkare rum, spegelblank fasad och kan störa mobilsignal. Prestige 70 släpper in 68 % dagsljus men blockerar ändå ca 50 % av solvärmen.",
  },
  {
    q: "Vad kostar 3M Prestige 70 monterat?",
    a: "Indikativt 1 100–1 600 kr/m² inklusive material, professionell installation och 10 års produktgaranti från 3M. Priset varierar med fönsterstorlek, antal rutor och arbetshöjd. Kostnadsfri offert efter platsbesök.",
  },
  {
    q: "Blir det mörkt inomhus med Prestige 70?",
    a: "Nej, Prestige 70 släpper igenom 68 % av det synliga ljuset (obehandlat 2-glas ligger runt 80–88 %). De flesta märker knappt själva filmen, men känner skillnaden när värmen och bländningen minskar.",
  },
  {
    q: "Vilken garanti gäller på 3M Prestige 70?",
    a: "10 års produktgaranti från 3M på materialet och 10 års installationsgaranti från BBN Nordic. Garantin täcker missfärgning, delaminering och bubbelbildning. Vid vissa isolerglas kan tillverkaren även utfärda glasgaranti. Vi kontrollerar vad som gäller för dina fönster.",
  },
  {
    q: "Finns Prestige 70 för utvändig montering?",
    a: "Ja. 3M Prestige 70 Exterior (även kallad PR 70x) monteras utvändigt när invändig film inte är möjlig, till exempel på vissa LowE-belagda glas. Vi bedömer rätt monteringssida vid platsbesöket.",
  },
];

export const Route = createFileRoute("/3m-prestige-70")({
  head: () => ({
    meta: [
      { title: "3M Prestige 70 — pris, specifikationer & montering | BBN Nordic" },
      {
        name: "description",
        content:
          "3M Prestige 70: klar nanokeramisk solfilm — 68 % VLT, 50 % TSER, 99 % UV-skydd. Auktoriserad 3M-installatör i Sverige sedan 2008. Officiella datablad, prisspann och kostnadsfri offert.",
      },
      { property: "og:title", content: "3M Prestige 70 — klar premium-solfilm" },
      {
        property: "og:description",
        content:
          "68 % dagsljus, 50 % värmereduktion, 99 % UV-skydd — utan spegelfasad. Auktoriserad 3M-installatör.",
      },
      { property: "og:url", content: "https://bbnnordic.se/3m-prestige-70" },
      { property: "og:image", content: BBN_PHOTOS.prestigeHero },
      { property: "og:type", content: "product" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/3m-prestige-70" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "3M Prestige 70 solfilm",
          category: "Fönsterfilm / Premium-solfilm",
          description:
            "3M Prestige 70 — nanokeramisk klar solfilm med 68 % ljusgenomsläpp, 50 % solenergireduktion och 99 % UV-blockering. Auktoriserad installation i Sverige.",
          image: BBN_PHOTOS.prestigeHero,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          url: "https://bbnnordic.se/3m-prestige-70",
          additionalProperty: [
            { "@type": "PropertyValue", name: "VLT", value: "68", unitText: "%" },
            { "@type": "PropertyValue", name: "TSER", value: "50", unitText: "%" },
            { "@type": "PropertyValue", name: "UV-blockering", value: "99", unitText: "%" },
            { "@type": "PropertyValue", name: "IR-blockering", value: "97", unitText: "%" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "1100",
            highPrice: "1600",
            priceSpecification: { "@type": "PriceSpecification", price: "1100", priceCurrency: "SEK" },
            availability: "https://schema.org/InStock",
            seller: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "3M Prestige", item: "https://bbnnordic.se/3m-prestige" },
            { "@type": "ListItem", position: 3, name: "Prestige 70", item: "https://bbnnordic.se/3m-prestige-70" },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="3M PRESTIGE 70"
        title="3M Prestige 70 — klar solfilm som släpper in ljuset men stoppar värmen"
        subtitle="Sveriges mest sålda solfilm i premiumsegmentet för bostäder och kontor. Du får 68 % ljusgenomsläpp, 50 % värmereduktion och 99 % UV-skydd utan spegelblank fasad eller mörka rum."
        image={BBN_PHOTOS.prestigeHero}
        breadcrumbs={[{ label: "3M Prestige", to: "/3m-prestige" }, { label: "Prestige 70" }]}
      />
      <PostHeroConversion />

      {/* Specifikationer */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">SPECIFIKATIONER</p>
            <h2 className="mt-3 text-3xl md:text-4xl max-w-3xl">
              Officiella värden från 3M:s produktblad (1/4" Clear glas).
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {specs.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.label} delay={i * 0.05}>
                  <div className="glass p-6 h-full">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded bg-gold/15 text-gold">
                      <Icon size={18} />
                    </div>
                    <div className="mt-4 font-display text-3xl text-gold-bright">{s.value}</div>
                    <div className="mt-1 text-sm font-semibold text-foreground">{s.label}</div>
                    <p className="mt-2 text-sm text-muted-foreground font-light">{s.note}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <p className="mt-6 flex items-start gap-2 text-xs text-muted-foreground max-w-3xl">
              <Info size={14} className="mt-0.5 shrink-0 text-gold" />
              Värden enligt 3M™ Sun Control Window Film Prestige 70 (ref. 1400301O).
              Exakta värden på 2-glas och tonat glas anges i offerten efter platsbesök.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pris */}
      <section className="py-16 md:py-20 bg-navy-light border-y border-border">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">PRIS</p>
            <h2 className="mt-3 text-3xl md:text-4xl">3M Prestige 70 pris — 1 100–1 600 kr/m² monterat</h2>
            <p className="mt-4 text-muted-foreground font-light max-w-2xl">
              Prisspannet gäller Skåne 2026 och inkluderar material, professionell installation
              av auktoriserad 3M-montör samt 10 års produktgaranti. Exakt pris efter kostnadsfri
              mätning. Fönsterstorlek, antal rutor och arbetshöjd påverkar priset.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/offert" className="btn-glow inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold">
                Få fast pris <ArrowRight size={16} />
              </Link>
              <Link to="/priser" className="inline-flex items-center gap-2 px-4 py-3 font-medium text-foreground hover:text-gold-bright">
                Se prislista
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Datablad */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">DATABLAD</p>
            <h2 className="mt-3 text-3xl md:text-4xl">Ladda ner 3M Prestige 70 produktblad</h2>
            <a
              href="/datablad/3m-prestige-70.pdf"
              className="mt-6 glass p-6 flex items-center gap-4 group max-w-2xl"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded bg-gold/15 text-gold shrink-0">
                <FileText size={22} />
              </span>
              <div className="flex-1">
                <div className="font-semibold text-foreground">3M Prestige 70 — Product Bulletin (PDF)</div>
                <div className="text-sm text-muted-foreground">Officiella specifikationer från 3M</div>
              </div>
              <ExternalLink size={18} className="text-gold group-hover:translate-x-1 transition-transform" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-navy-light border-t border-border">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">VANLIGA FRÅGOR</p>
            <h2 className="mt-3 text-3xl md:text-4xl">3M Prestige 70 — vanliga frågor</h2>
          </Reveal>
          <div className="mt-8 space-y-4">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <details className="glass p-5 group">
                  <summary className="cursor-pointer font-semibold text-foreground list-none flex justify-between items-start gap-4">
                    {f.q}
                    <span className="text-gold shrink-0 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-muted-foreground font-light leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 flex flex-wrap gap-4 text-sm">
              <Link to="/3m-prestige" className="text-gold-bright hover:underline">← Alla Prestige-varianter</Link>
              <Link to="/3m-prestige-40-exterior" className="text-gold-bright hover:underline">3M Prestige 40 Exterior →</Link>
              <Link to="/solfilm-bast-i-test" className="text-gold-bright hover:underline">Solfilm bäst i test →</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
